import { AlertController } from 'ionic-angular';
import { HttpProvider } from '../http/http';
import { LoggerProvider } from '../logger/logger';
import { StorageProvider } from '../storage/storage';
import { StorageType } from '../../model/types/storage-type';
import { RmtSnackbarComponent } from '../../components/rmt-snackbar/rmt-snackbar';
import { SegmentProvider } from '../analytics/segment';
import { AnalyticsEventType } from '../../model/types/analytics.const';
import { APP_CONFIG, Region } from '../../environment';
import { regions } from '../../environment/region/regions';
import { ResourceProvider } from '../resource/resource';

export interface HiddenFeature {
  name: string;
  description: string;
  activate: () => void;
  deactivate: () => void;
}

export interface AppliedFeaturesDiff {
  newlyEnabled: string[];
  newlyDisabled: string[];
}

const DEFAULT_MIN_VERSION = HttpProvider.minVersion;

const SN_IS_EU_ORIGINAL = regions.SN.isEu;
const SN_ALLOWED_ONLY_ORIGINAL = regions.SN.originalCountryAllowedOnly;
let originalGetCountries: typeof ResourceProvider.prototype.getCountries | null = null;

export const PASSPHRASE_REGISTRY: { [passphrase: string]: HiddenFeature } = {
  'betafive': {
    name: 'Beta 5.0.0',
    description: 'Sets minVersion to 5.0.0; enables SN UAT region',
    activate: () => {
      HttpProvider.minVersion = HttpProvider.betaVersion;

      if (!APP_CONFIG.regions.find(r => r.region === Region.SN)) {
        APP_CONFIG.regions.push(regions.SN);
      }
      regions.SN.isEu = true;
      regions.SN.originalCountryAllowedOnly = false;

      if (!originalGetCountries) {
        originalGetCountries = ResourceProvider.prototype.getCountries;
        ResourceProvider.prototype.getCountries = async function () {
          const result = await originalGetCountries!.call(this);
          if (Array.isArray(result) && !result.find((c: any) => c.value === 'SN')) {
            result.push({ value: 'SN', text: 'Senegal', countryCode: '221', isRegistrationEnabled: true });
          }
          return result;
        };
      }
    },
    deactivate: () => {
      HttpProvider.minVersion = DEFAULT_MIN_VERSION;

      const idx = APP_CONFIG.regions.findIndex(r => r.region === Region.SN);
      if (idx !== -1) APP_CONFIG.regions.splice(idx, 1);
      regions.SN.isEu = SN_IS_EU_ORIGINAL;
      regions.SN.originalCountryAllowedOnly = SN_ALLOWED_ONLY_ORIGINAL;

      if (originalGetCountries) {
        ResourceProvider.prototype.getCountries = originalGetCountries;
        originalGetCountries = null;
      }
    },
  }
};

export class HiddenMenuProvider {
  // Persisted intent: features that will be active on next app launch.
  private static savedFeatures: string[] = [];
  // Features whose activate() actually ran during this session (set at init).
  private static appliedFeatures: string[] = [];
  private static tapCount: number = 0;
  private static tapTimer: any = null;

  static getSavedPassphrases(): string[] {
    return this.savedFeatures.slice();
  }

  static getAppliedPassphrases(): string[] {
    return this.appliedFeatures.slice();
  }

  static isSaved(passphrase: string): boolean {
    return this.savedFeatures.indexOf(passphrase) !== -1;
  }

  static isApplied(passphrase: string): boolean {
    return this.appliedFeatures.indexOf(passphrase) !== -1;
  }

  // Apply stored features at app launch: runs activate() for each stored passphrase,
  // sets appliedFeatures, and returns the diff against the previous session's applied set.
  static applyFromStorage(stored: string[], lastApplied: string[]): AppliedFeaturesDiff {
    const storedValid = (stored || []).filter(p => !!PASSPHRASE_REGISTRY[p]);
    const lastAppliedValid = (lastApplied || []).filter(p => !!PASSPHRASE_REGISTRY[p]);

    this.savedFeatures = storedValid.slice();
    this.appliedFeatures = [];

    for (const passphrase of storedValid) {
      const feature = PASSPHRASE_REGISTRY[passphrase];
      feature.activate();
      this.appliedFeatures.push(passphrase);
      LoggerProvider.info('HiddenMenuProvider', `Applied hidden feature: ${feature.name}`);
    }

    const newlyEnabled = storedValid.filter(p => lastAppliedValid.indexOf(p) === -1);
    const newlyDisabled = lastAppliedValid.filter(p => storedValid.indexOf(p) === -1);
    return { newlyEnabled, newlyDisabled };
  }

  private static stageActivation(passphrase: string): HiddenFeature | null {
    const feature = PASSPHRASE_REGISTRY[passphrase];
    if (!feature) return null;
    if (this.isSaved(passphrase)) return feature;
    this.savedFeatures.push(passphrase);
    LoggerProvider.info('HiddenMenuProvider', `Staged activation: ${feature.name}`);
    return feature;
  }

  private static stageDeactivation(passphrase: string): HiddenFeature | null {
    const feature = PASSPHRASE_REGISTRY[passphrase];
    if (!feature) return null;
    const idx = this.savedFeatures.indexOf(passphrase);
    if (idx === -1) return feature;
    this.savedFeatures.splice(idx, 1);
    LoggerProvider.info('HiddenMenuProvider', `Staged deactivation: ${feature.name}`);
    return feature;
  }

  static handleSecretTap(alertCtrl: AlertController, storageProvider: StorageProvider, httpProvider: HttpProvider, analytics: SegmentProvider) {
    this.tapCount++;
    if (this.tapTimer) {
      clearTimeout(this.tapTimer);
    }
    this.tapTimer = setTimeout(() => {
      this.tapCount = 0;
    }, 3000);

    if (this.tapCount >= 7) {
      this.tapCount = 0;
      clearTimeout(this.tapTimer);
      this.showMenu(alertCtrl, storageProvider, httpProvider, analytics);
    }
  }

  private static getFeatureState(passphrase: string): 'active' | 'pending-activation' | 'pending-deactivation' | null {
    const saved = this.isSaved(passphrase);
    const applied = this.isApplied(passphrase);
    if (saved && applied) return 'active';
    if (saved && !applied) return 'pending-activation';
    if (!saved && applied) return 'pending-deactivation';
    return null;
  }

  private static stateLabel(state: 'active' | 'pending-activation' | 'pending-deactivation'): string {
    switch (state) {
      case 'active': return 'Active';
      case 'pending-activation': return 'Pending — restart app to enable';
      case 'pending-deactivation': return 'Pending — restart app to disable';
    }
  }

  private static showMenu(alertCtrl: AlertController, storageProvider: StorageProvider, httpProvider: HttpProvider, analytics: SegmentProvider) {
    const seen: { [p: string]: boolean } = {};
    const union: string[] = [];
    for (const p of this.savedFeatures) { if (!seen[p]) { seen[p] = true; union.push(p); } }
    for (const p of this.appliedFeatures) { if (!seen[p]) { seen[p] = true; union.push(p); } }

    analytics.trackEvent({
      eventName: AnalyticsEventType.HiddenMenuOpened,
      analyticsData: { activeFeatures: this.savedFeatures.join(', ') || 'none' }
    });

    let message = '';
    if (union.length > 0) {
      message = '<div style="text-align:left;margin-bottom:8px;font-weight:600;">Features:</div>';
      for (const p of union) {
        const feature = PASSPHRASE_REGISTRY[p];
        const state = this.getFeatureState(p);
        if (!feature || !state) continue;
        const label = this.stateLabel(state);
        message += `<div style="padding:4px 0;font-size:14px;">&bull; ${feature.name} <span style="color:#999;font-size:12px;">(${label})</span></div>`;
      }
    }

    const buttons: any[] = [];

    for (const p of union) {
      const feature = PASSPHRASE_REGISTRY[p];
      const state = this.getFeatureState(p);
      if (!feature || !state) continue;

      if (state === 'pending-deactivation') {
        buttons.push({
          text: `Restore: ${feature.name}`,
          handler: () => {
            this.stageActivation(p);
            this.persist(storageProvider);
            analytics.trackEvent({
              eventName: AnalyticsEventType.HiddenMenuFeatureActivated,
              analyticsData: { featureName: feature.name, passphrase: p }
            });
            RmtSnackbarComponent.present(`Restored: ${feature.name}. Restart the app to apply.`);
          }
        });
      } else {
        buttons.push({
          text: `Remove: ${feature.name}`,
          handler: () => {
            this.stageDeactivation(p);
            this.persist(storageProvider);
            analytics.trackEvent({
              eventName: AnalyticsEventType.HiddenMenuFeatureDeactivated,
              analyticsData: { featureName: feature.name, passphrase: p }
            });
            RmtSnackbarComponent.present(`Removed: ${feature.name}. Restart the app to fully disable.`);
          }
        });
      }
    }

    buttons.push({
      text: 'Close',
      role: 'cancel'
    });

    buttons.push({
      text: 'Submit',
      handler: (data) => {
        const passphrase = (data.passphrase || '').trim().toLowerCase();
        if (!passphrase) return false;

        if (this.isSaved(passphrase)) {
          RmtSnackbarComponent.present('Feature already staged');
          return false;
        }

        const feature = this.stageActivation(passphrase);
        if (feature) {
          this.persist(storageProvider);
          analytics.trackEvent({
            eventName: AnalyticsEventType.HiddenMenuFeatureActivated,
            analyticsData: { featureName: feature.name, passphrase: passphrase }
          });
          RmtSnackbarComponent.present(`Staged: ${feature.name}. Restart the app to fully enable.`);
        } else {
          RmtSnackbarComponent.present('Invalid passphrase');
          return false;
        }
      }
    });

    const alert = alertCtrl.create({
      title: 'Hidden Menu',
      message: message,
      inputs: [{
        name: 'passphrase',
        type: 'text',
        placeholder: 'Enter passphrase',
      }],
      buttons: buttons,
      enableBackdropDismiss: true,
      cssClass: 'hidden-menu-alert'
    });

    alert.present().then(() => {
      const inputEl = document.querySelector('.hidden-menu-alert input');
      if (inputEl) {
        inputEl.setAttribute('autocomplete', 'off');
        inputEl.setAttribute('autocorrect', 'off');
        inputEl.setAttribute('autocapitalize', 'off');
        inputEl.setAttribute('spellcheck', 'false');
      }
    });
  }

  private static persist(storageProvider: StorageProvider) {
    storageProvider.setJson(StorageType.HiddenMenuFeatures, this.getSavedPassphrases());
  }
}



// WEBPACK FOOTER //
// ./src/providers/hidden-menu/hidden-menu.ts